import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import toast from 'react-hot-toast';

const EXPORT_TYPES = [
    { label: 'Valid', value: 'Valid' },
    { label: 'Invalid', value: 'Invalid' },
    { label: 'Catch All', value: 'Catch All' },
    { label: 'Unknown', value: 'Unknown' },
    { label: 'Disposable', value: 'Disposable' },
    { label: 'Free Account', value: 'Free Account' },
    { label: 'Role Account', value: 'Role Account' },
];

export default function CSVExportModal({ open, onClose, selectedJob, selectedTags, selectedFilters, allowNoJob = false }) {
    const [mainType, setMainType] = useState('ALL');
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [error, setError] = useState('');

    // Lock body scroll when modal is open
    useEffect(() => {
        if (open) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [open]);

    if (!open) return null;

    const handleTypeChange = (type) => {
        setSelectedTypes((prev) => {
            const next = prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type];
            if (next.length > 0 && mainType !== '') {
                setMainType('');
            }
            return next;
        });
    };

    const handleExport = async () => {
        setError('');
        if (!allowNoJob && (!selectedJob || !selectedJob.job_id || !selectedJob.job_name)) {
            setError('No list selected for export.');
            return;
        }
        let typesToSend = [];
        if (mainType === 'ALL') typesToSend = ['ALL'];
        else if (mainType === 'ALL_DELIVERABLES') typesToSend = ['ALL_DELIVERABLES'];
        else if (selectedTypes.length > 0) typesToSend = selectedTypes;
        else {
            setError('Please select at least one type to export.');
            return;
        }

        const serverFilters = { ...selectedFilters };
        if (!typesToSend.includes('ALL') && !typesToSend.includes('ALL_DELIVERABLES')) {
            if (typesToSend.length === 1) {
                serverFilters.status = typesToSend[0];
            }
        }

        try {
            const res = await api.post('/export', {
                job_name: selectedJob?.job_name || 'All Leads',
                list_id: selectedJob?.job_id,
                tags: selectedTags,
                filters: { ...serverFilters, types: typesToSend },
            });
            if (res.data && res.data.success === false) {
                setError(res.data && res.data.message ? res.data.message : 'Failed to start export.');
                return;
            }
            toast.success('Export started! Track progress in Download Lists.');
            onClose();
        } catch (e) {
            setError('Failed to start export.');
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal">
                <h2 className="text-xl font-bold mb-4">Export List</h2>
                <div className="mb-4 text-sm">
                    {(!allowNoJob && !selectedJob?.job_name) ? (
                        <span className="text-red-500 font-bold">No list selected</span>
                    ) : selectedJob?.job_name ? (
                        <>
                            <span className="font-semibold">List:</span> {selectedJob.job_name}
                        </>
                    ) : (
                        <span className="font-semibold">Exporting all leads</span>
                    )}
                </div>

                <div className="flex flex-col gap-4 mb-6">
                    <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="radio" checked={mainType === 'ALL'} onChange={() => setMainType('ALL')} />
                            <span>All</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="radio" checked={mainType === 'ALL_DELIVERABLES'} onChange={() => setMainType('ALL_DELIVERABLES')} />
                            <span>All Deliverable</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="radio" checked={mainType === ''} onChange={() => setMainType('')} />
                            <span>Custom</span>
                        </label>
                    </div>
                    <div className="flex flex-wrap gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                        {EXPORT_TYPES.map((type) => (
                            <label key={type.value} className={`flex items-center gap-2 text-sm cursor-pointer ${mainType !== '' ? 'opacity-50 grayscale pointer-events-none' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={selectedTypes.includes(type.value)}
                                    onChange={() => handleTypeChange(type.value)}
                                    disabled={mainType !== ''}
                                />
                                <span>{type.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {error && <div className="text-danger-600 text-xs mb-4 font-bold">{error}</div>}

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                    <button onClick={onClose} className="btn btn-secondary">Close</button>
                    <button onClick={handleExport} className="btn btn-primary">Export</button>
                </div>
            </div>
        </div>
    );
}
